(function () {
  const DRAG_START_THRESHOLD = 3;
  const DRAG_CLICK_THRESHOLD = 4;
  const FADE_SLACK = 6;
  const EDGE_SLACK = 2;

  const PANEL_MARKUP = [
    '<div class="apx-panel__backdrop" data-apx-close></div>',
    '<div class="apx-panel__box">',
    '<div class="apx-panel__photo"><img alt="" decoding="async"></div>',
    '<div class="apx-panel__head">',
    '<span class="apx-row"><s class="apx-row__key">Owner</s><span class="apx-row__value" data-apx-panel-owner></span></span>',
    '<span class="apx-row"><s class="apx-row__key">Rated</s><span class="apx-row__value apx-row__value--red" data-apx-panel-rated></span></span>',
    '<span class="apx-row"><s class="apx-row__key">Date</s><span class="apx-row__value" data-apx-panel-date></span></span>',
    "</div>",
    '<div class="apx-panel__text" data-more="0">',
    '<div class="apx-panel__scroll"><p class="apx-panel__body" data-apx-panel-body></p></div>',
    '<div class="apx-panel__foot">',
    '<button class="apx-panel__step" type="button" data-apx-step="-1">‹ Previous</button>',
    '<span class="apx-panel__gap"></span>',
    '<button class="apx-panel__step" type="button" data-apx-step="1">Next ›</button>',
    "</div>",
    "</div>",
    '<button class="apx-panel__close" type="button" data-apx-close aria-label="Close review">✕</button>',
    "</div>",
  ].join("");

  class ApexReviews {
    constructor(root) {
      this.root = root;
      this.rail = root.querySelector("[data-apx-rail]");
      this.cards = Array.from(root.querySelectorAll("[data-apx-card]"));
      this.arrows = Array.from(root.querySelectorAll("[data-apx-arrow]"));
      this.panel = null;
      this.current = 0;
      this.openedBy = null;
      this.drag = { active: false, moved: 0, startX: 0, startScrollLeft: 0 };
      this.listeners = [];
      this.bind();
    }

    on(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.listeners.push([target, type, handler, options]);
    }

    bind() {
      if (!this.rail || !this.cards.length) return;

      this.on(this.rail, "click", (event) => {
        if (this.drag.moved > DRAG_CLICK_THRESHOLD) return;
        const card = event.target.closest("[data-apx-card]");
        if (card) this.open(this.cards.indexOf(card), card);
      });

      this.on(this.rail, "keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        const card = event.target.closest("[data-apx-card]");
        if (!card) return;
        event.preventDefault();
        this.open(this.cards.indexOf(card), card);
      });

      this.on(this.rail, "pointerdown", (event) => {
        if (event.pointerType === "touch") return;
        this.drag.active = true;
        this.drag.moved = 0;
        this.drag.startX = event.clientX;
        this.drag.startScrollLeft = this.rail.scrollLeft;
      });

      this.on(window, "pointermove", (event) => {
        if (!this.drag.active) return;
        const shift = event.clientX - this.drag.startX;
        this.drag.moved = Math.max(this.drag.moved, Math.abs(shift));
        if (this.drag.moved > DRAG_START_THRESHOLD) {
          this.rail.classList.add("is-dragging");
          this.rail.scrollLeft = this.drag.startScrollLeft - shift;
          event.preventDefault();
        }
      });

      this.on(window, "pointerup", () => {
        this.drag.active = false;
        this.rail.classList.remove("is-dragging");
        window.setTimeout(() => {
          this.drag.moved = 0;
        }, 0);
      });

      this.arrows.forEach((arrow) => {
        this.on(arrow, "click", () => this.scrollRail(Number(arrow.dataset.apxArrow)));
      });
      this.on(this.rail, "scroll", () => this.updateArrows(), { passive: true });
      this.on(window, "resize", () => this.updateArrows());
      this.updateArrows();
    }

    scrollRail(direction) {
      const firstCard = this.cards[0];
      const railGap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
      const cardStep = firstCard ? firstCard.getBoundingClientRect().width + railGap : this.rail.clientWidth;
      const cardsPerView = Math.max(1, Math.floor((this.rail.clientWidth + railGap) / cardStep));
      this.rail.scrollBy({ left: direction * cardsPerView * cardStep, behavior: "smooth" });
    }

    updateArrows() {
      const maximumScroll = this.rail.scrollWidth - this.rail.clientWidth;
      const canScroll = maximumScroll > EDGE_SLACK;
      this.arrows.forEach((arrow) => {
        const direction = Number(arrow.dataset.apxArrow);
        const isAtEdge =
          direction < 0
            ? this.rail.scrollLeft <= EDGE_SLACK
            : this.rail.scrollLeft >= maximumScroll - EDGE_SLACK;
        arrow.hidden = !canScroll;
        arrow.disabled = isAtEdge;
      });
    }

    buildPanel() {
      if (this.panel) return;

      const panel = document.createElement("div");
      panel.className = "apx-panel";
      panel.setAttribute("data-on", "0");
      panel.setAttribute("role", "dialog");
      panel.setAttribute("aria-modal", "true");
      panel.setAttribute("aria-label", "Customer review");
      panel.setAttribute("data-apx-skin", this.root.dataset.apxSkin || "light");
      panel.innerHTML = PANEL_MARKUP;
      document.body.appendChild(panel);

      this.panel = panel;
      this.panelPhoto = panel.querySelector(".apx-panel__photo img");
      this.panelOwner = panel.querySelector("[data-apx-panel-owner]");
      this.panelRated = panel.querySelector("[data-apx-panel-rated]");
      this.panelDate = panel.querySelector("[data-apx-panel-date]");
      this.panelBody = panel.querySelector("[data-apx-panel-body]");
      this.panelText = panel.querySelector(".apx-panel__text");
      this.panelScroll = panel.querySelector(".apx-panel__scroll");
      this.panelClose = panel.querySelector(".apx-panel__close");

      this.on(panel, "click", (event) => {
        if (event.target.closest("[data-apx-close]")) {
          this.close();
          return;
        }
        const step = event.target.closest("[data-apx-step]");
        if (step) this.go(Number(step.dataset.apxStep));
      });

      this.on(panel, "keydown", (event) => {
        if (event.key !== "Tab") return;
        const focusable = [this.panelClose].concat(
          Array.from(panel.querySelectorAll("[data-apx-step]"))
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });

      this.on(this.panelScroll, "scroll", () => this.updateFade());

      this.on(
        document,
        "keydown",
        (event) => {
          if (this.panel.getAttribute("data-on") !== "1") return;
          if (event.key === "Escape") {
            event.stopPropagation();
            this.close();
          } else if (event.key === "ArrowLeft") {
            event.stopPropagation();
            this.go(-1);
          } else if (event.key === "ArrowRight") {
            event.stopPropagation();
            this.go(1);
          }
        },
        true
      );

      this.on(window, "resize", () => {
        if (this.panel.getAttribute("data-on") !== "1") return;
        this.updateFade();
        this.fitMedia();
      });
    }

    open(index, card) {
      if (index < 0) return;
      this.buildPanel();
      this.current = index;
      this.openedBy = card || null;
      this.paint();
      this.panel.setAttribute("data-on", "1");
      this.lockPage();
      this.panelClose.focus();
    }

    go(step) {
      const total = this.cards.length;
      this.current = (this.current + step + total) % total;
      this.paint();
    }

    paint() {
      const card = this.cards[this.current];
      if (!card) return;

      const photo = card.querySelector(".apx-card__photo img");
      if (photo) {
        this.panelPhoto.src = photo.currentSrc || photo.src;
        this.panelPhoto.alt = photo.alt;
      } else {
        this.panelPhoto.removeAttribute("src");
        this.panelPhoto.alt = "";
      }
      this.panelPhoto.parentElement.setAttribute(
        "data-apx-media",
        card.dataset.apxMedia || "photo"
      );

      this.panelOwner.textContent = card.dataset.apxOwner || "";
      this.panelDate.textContent = card.dataset.apxDate || "";

      const rated = card.querySelector("[data-apx-rated-source]");
      this.panelRated.innerHTML = rated ? rated.innerHTML : "";

      this.panelBody.textContent = card.querySelector(".apx-card__say").textContent.trim();
      this.panelScroll.scrollTop = 0;
      window.requestAnimationFrame(() => {
        this.updateFade();
        this.fitMedia();
      });
      if (!this.panelPhoto.complete) {
        this.panelPhoto.addEventListener("load", () => this.fitMedia(), { once: true });
      }
    }

    fitMedia() {
      const box = this.panel.querySelector(".apx-panel__box");
      const isVideo = this.panelPhoto.parentElement.dataset.apxMedia === "video";
      const narrow = window.matchMedia("(max-width: 660px)").matches;
      const ratio = this.panelPhoto.naturalWidth / this.panelPhoto.naturalHeight;

      if (!isVideo || narrow || !ratio || !isFinite(ratio)) {
        box.style.gridTemplateColumns = "";
        return;
      }

      const boxWidth = box.clientWidth;
      const widest = boxWidth * 0.55;
      const narrowest = Math.min(boxWidth * 0.3, 260);
      const fitted = Math.max(narrowest, Math.min(widest, box.clientHeight * ratio));
      box.style.gridTemplateColumns = Math.round(fitted) + "px minmax(0, 1fr)";
    }

    updateFade() {
      const scroll = this.panelScroll;
      const hasMore =
        scroll.scrollTop < scroll.scrollHeight - scroll.clientHeight - FADE_SLACK;
      this.panelText.setAttribute("data-more", hasMore ? "1" : "0");
    }

    lockPage() {
      window.pageScrollLock.lock(this);
    }

    unlockPage() {
      window.pageScrollLock.unlock(this);
    }

    close() {
      if (!this.panel || this.panel.getAttribute("data-on") !== "1") return;
      this.panel.setAttribute("data-on", "0");
      this.unlockPage();
      const card = this.openedBy;
      if (card && card.matches && card.matches(":focus-visible")) card.focus();
      this.openedBy = null;
    }

    destroy() {
      this.close();
      this.listeners.forEach(([target, type, handler, options]) =>
        target.removeEventListener(type, handler, options)
      );
      this.listeners = [];
      if (this.panel) this.panel.remove();
      this.panel = null;
      delete this.root.apexReviews;
    }
  }

  const REVIEW_DATE_ATTRIBUTE = "data-apx-review-date";
  const LONG_NUMBER_CLASS = "apx-deck__number--long";

  const formatReviewDate = (isoDate) => {
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return null;
    const isThisYear = date.getFullYear() === new Date().getFullYear();
    const options = isThisYear
      ? { month: "short", day: "2-digit" }
      : { month: "short", day: "2-digit", year: "numeric" };
    return { text: date.toLocaleDateString("en-US", options).toUpperCase(), isThisYear };
  };

  const showDatesInVisitorTime = (root) => {
    root.querySelectorAll(`[${REVIEW_DATE_ATTRIBUTE}]`).forEach((element) => {
      const formattedDate = formatReviewDate(element.getAttribute(REVIEW_DATE_ATTRIBUTE));
      if (!formattedDate) return;
      element.textContent = formattedDate.text;
      const card = element.closest("[data-apx-card]");
      if (card) card.dataset.apxDate = formattedDate.text;
      if (element.classList.contains("apx-deck__number")) {
        element.classList.toggle(LONG_NUMBER_CLASS, !formattedDate.isThisYear);
      }
    });
  };

  const start = (root) => {
    if (root.apexReviews) return;
    showDatesInVisitorTime(root);
    root.apexReviews = new ApexReviews(root);
  };

  const startAll = (scope) =>
    (scope || document).querySelectorAll("[data-apx-reviews]").forEach(start);

  startAll();

  document.addEventListener("shopify:section:load", (event) => startAll(event.target));

  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll("[data-apx-reviews]").forEach((root) => {
      if (root.apexReviews) root.apexReviews.destroy();
    });
  });
})();
